import React from "react";
import * as Yup from "yup";
import { EDIT_EMERGENCY_CONTACT_DETAILS, TYPE_OF_RELATION } from "./constants";
import { Form, Formik } from "formik";
import Button from "../../Molecules/button/Button";
import Input from "../../Atoms/Input/Input";
import ContactInput from "../../Atoms/Input/ContactInput";
import SelectDropdown from "../../Atoms/Select/Select";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";

interface EditEmergencyContactProps {
  defaultSelectedData?: any;
  handleOnSubmittedData?: (formData: any) => void;
  handleClose: () => void;
}

const EditEmergencyContact = ({
  defaultSelectedData,
  handleOnSubmittedData,
  handleClose,
}: EditEmergencyContactProps) => {
  const validationSchema = Yup.object().shape({
    [EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "Contact name can only contain characters"
      )
      .required("Contact name is required"),
    [EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_RELATION]: Yup.string().required(
      "Contact relation is required"
    ),
    [EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_EMAIL]: Yup.string()
      // .email("Enter a valid email address")
      .matches(VALIDATION_CONSTANTS.EMAIL, "Enter a valid email address")
      .required("Email is required"),
    [EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("Contact number is required"),
  });

  const commonGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px 16px",
  };

  return (
    <Formik
      initialValues={{
        [EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NAME]: `${defaultSelectedData?.contactName ? defaultSelectedData?.contactName : ""}`,
        [EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_RELATION]: `${
          defaultSelectedData?.contactRelation
            ? defaultSelectedData?.contactRelation
            : ""
        }`,
        [EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_EMAIL]: `${defaultSelectedData?.contactEmail ? defaultSelectedData?.contactEmail : ""}`,
        [EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NUMBER]: `${
          defaultSelectedData?.contactNumber
            ? defaultSelectedData?.contactNumber
            : ""
        }`,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        try {
          handleOnSubmittedData && handleOnSubmittedData(values);
          // Handle form data here
          // If needed, call an API or further process the values here
        } catch (error) {
          console.log(error);
        } finally {
          setSubmitting(false); // Reset submitting state
        }
      }}
    >
      {({
        errors,
        submitForm,
        isSubmitting,
        handleReset,
        isValid,
        values,
        touched,
        setFieldValue,
        handleBlur,
        handleSubmit,
      }) => {
        return (
          <Form
            onSubmit={handleSubmit}
            className="h-full flex flex-col justify-between rounded-lg"
          >
            <div className="w-full flex flex-col rounded-lg">
              {/* PATIENT DETAILS */}
              <div>
                {/* <Heading title="Patient Details" customStyle={headerStyle} /> */}
                <div style={commonGridStyle}>
                  <Input
                    // disabled
                    textFieldLabel="Contact Name"
                    name={EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NAME}
                    value={values[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NAME,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NAME] &&
                      Boolean(
                        errors[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NAME]
                      )
                    }
                    helperText={
                      touched[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NAME]
                        ? errors[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NAME]
                        : undefined
                    }
                  />
                  <SelectDropdown
                    label="Contact Relation"
                    isStarPresent={true}
                    value={
                      values[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_RELATION]
                    }
                    options={TYPE_OF_RELATION}
                    onChangeCb={(e) =>
                      setFieldValue(
                        EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_RELATION,
                        e.target.value
                      )
                    }
                    error={
                      touched[
                        EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_RELATION
                      ] &&
                      Boolean(
                        errors[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_RELATION]
                      )
                    }
                    helperText={
                      touched[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_RELATION]
                        ? errors[
                            EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_RELATION
                          ]
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="Contact Email"
                    name={EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_EMAIL}
                    value={values[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_EMAIL]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_EMAIL,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_EMAIL] &&
                      Boolean(
                        errors[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_EMAIL]
                      )
                    }
                    helperText={
                      touched[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_EMAIL]
                        ? errors[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_EMAIL]
                        : undefined
                    }
                  />
                  <ContactInput
                    // disabled
                    textFieldLabel="Contact Number"
                    name={EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NUMBER}
                    value={
                      values[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NUMBER]
                    }
                    onChangeCb={(value) => {
                      setFieldValue(
                        EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NUMBER,
                        value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NUMBER] &&
                      Boolean(
                        errors[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NUMBER]
                      )
                    }
                    helperText={
                      touched[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NUMBER]
                        ? errors[EDIT_EMERGENCY_CONTACT_DETAILS.CONTACT_NUMBER]
                        : undefined
                    }
                    isContactNumber={true}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-10 gap-4">
              <Button
                label={TEXT_CONSTANTS?.CANCEL}
                variant="outlined"
                onClickCb={() => {
                  handleClose();
                }}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  backgroundColor: "white",
                  border: "none",
                  color: "#393939",
                }}
              />
              <Button
                // disabled={!isValid} // Button disabled if form is invalid or submitting
                type="submit"
                label={TEXT_CONSTANTS?.SAVE}
                variant="contained"
                onClickCb={() => {}}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                }}
              ></Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EditEmergencyContact;
